import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const SupplierNetwork: React.FC = () => {
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    gsap.utils.toArray('.supplier-card').forEach((card: any, index) => {
      gsap.fromTo(card, 
        { y: 50, opacity: 0, rotationY: 15 },
        {
          y: 0,
          opacity: 1,
          rotationY: 0,
          duration: 0.8,
          delay: index * 0.1,
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            end: 'bottom 15%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const suppliers = [
    {
      name: "Artisan Wine Cellars",
      category: "Premium Wines",
      location: "Napa Valley, CA",
      description: "Boutique winery specializing in small-batch, handcrafted wines",
      image: "https://images.unsplash.com/photo-1562601579-599dec564e06?q=80&w=400&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    },
    {
      name: "Farm Fresh Organics",
      category: "Organic Produce",
      location: "Central Valley, CA",
      description: "Certified organic farm providing seasonal vegetables and fruits",
      image: "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "Prime Cuts Butchery",
      category: "Premium Meats",
      location: "Texas, USA",
      description: "Artisan butchery specializing in grass-fed and heritage breed meats",
      image: "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "Mediterranean Delights",
      category: "Imported Specialties",
      location: "Italy & Spain",
      description: "Authentic Mediterranean products including olive oils, cheeses, and cured meats",
      image: "https://images.unsplash.com/photo-1551218808-94e220e084d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "Seafood Masters",
      category: "Fresh Seafood",
      location: "Pacific Northwest",
      description: "Sustainable seafood sourcing from certified fisheries",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      name: "Spice Traders Co.",
      category: "Exotic Spices",
      location: "Global Sourcing",
      description: "Rare and exotic spices from around the world",
      image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    }
  ];

  return (
    <section id="suppliers" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="section-header animate-on-scroll">
          <h2 className="heading-lg heading-on-light mb-6">
            Our Supplier Network
          </h2>
          <p className="text-body-lg text-on-light max-w-3xl mx-auto">
            Connect with verified suppliers from around the world, offering premium ingredients and exceptional service to elevate your culinary operations.
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="stat-number mb-2">50+</div>
            <div className="stat-label text-on-light">Verified Suppliers</div>
          </div>
          <div className="text-center">
            <div className="stat-number mb-2">15</div>
            <div className="stat-label text-on-light">Countries</div>
          </div>
          <div className="text-center">
            <div className="stat-number mb-2">1000+</div>
            <div className="stat-label text-on-light">Products</div>
          </div>
          <div className="text-center">
            <div className="stat-number mb-2">24/7</div>
            <div className="stat-label text-on-light">Support</div>
          </div>
        </div>

        {/* Suppliers Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {suppliers.map((supplier, index) => (
            <div key={index} className="supplier-card bg-white rounded-lg shadow-lg border border-accent overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="relative">
                <img 
                  src={supplier.image} 
                  alt={supplier.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                    {supplier.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-text mb-2 roboto-bold">{supplier.name}</h3>
                <p className="text-text-accent text-sm mb-3 flex items-center roboto-regular">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {supplier.location}
                </p>
                <p className="text-text-accent text-sm roboto-regular">{supplier.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Join Network CTA */}
        <div className="cta-container-dark">
          <h3 className="heading-md heading-on-dark mb-6">
            Join Our Supplier Network
            </h3>
          <p className="text-body text-on-dark cta-content mb-8">
            Are you a premium food supplier? Join our exclusive network and connect with top-tier restaurants and culinary establishments.
            </p>
          <a href="#contact" className="btn-primary text-lg px-10 py-4">
              Apply to Join
            </a>
        </div>
      </div>
    </section>
  );
};

export default SupplierNetwork;
